import type { WebsiteEventGoal } from 'types';
import { useState } from 'react';
import { toast } from 'sonner';
import { useWebsiteDetails } from '@/state/useWebsiteDetails';
import { apiErrorMessage } from '../api-error';

type MappedRole = 'primary' | 'secondary';

type MappingPrompt = { goalId: number; name: string; role: MappedRole };

type OfferParams = { name: string; role: MappedRole };

/**
 * After an experiment picks an event its website has not mapped (role `ignored`), offers to map it
 * in the role it was picked for, so it is offered first next time and seeds new experiments if
 * secondary. The objective itself is already saved; this only asks about the website's mapping,
 * which is a separate, website-wide decision and so never made silently.
 *
 * Only events the website has a row for can be mapped. An untracked name gets no prompt.
 */
export const useObjectiveMapping = (websiteId: number) => {
  const { eventGoals, updateEventGoalRole } = useWebsiteDetails(websiteId);
  const [prompt, setPrompt] = useState<MappingPrompt | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const findIgnored = (name: string): WebsiteEventGoal | undefined =>
    (eventGoals ?? []).find((goal) => goal.role === 'ignored' && goal.name?.trim() === name);

  /** Call after picking `name` as `role`; asks only when the website has it unmapped. */
  const offer = ({ name, role }: OfferParams) => {
    const goal = findIgnored(name);
    setPrompt(goal ? { goalId: goal.id, name, role } : null);
  };

  const dismiss = () => setPrompt(null);

  const accept = async () => {
    if (!prompt) return;
    setIsSaving(true);
    try {
      await updateEventGoalRole(prompt.goalId, prompt.role);
      toast.success(`Mapped ${prompt.name} as ${prompt.role} on this website.`);
      setPrompt(null);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Could not save the mapping.'));
    } finally {
      setIsSaving(false);
    }
  };

  return { prompt, isSaving, offer, accept, dismiss };
};

export type ObjectiveMappingManager = ReturnType<typeof useObjectiveMapping>;
